import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import { Button, Card, EmptyState, Skeleton } from '../components/ui'
import { useGroupsTree, usePerson } from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { GroupTreePicker } from '../components/features/GroupTreePicker'
import { readOnlyReason } from '../lib/dryrun'
import { groupPaths } from './Groups'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** Group-membership editing for one person (`/persons/{uid}/membership`) —
 *  a SENSITIVE change, so it lives on its own page behind an explicit
 *  Save, never inline on the person page (which links here). Save is the
 *  same full-replace PUT the person card used: every field resent with the
 *  new groups list. */
export default function PersonMembershipPage() {
  const { uid = '' } = useParams()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const person = usePerson(uid)
  const groupsTree = useGroupsTree()
  const groupPathMap = useMemo(() => groupPaths(groupsTree.data ?? []), [groupsTree.data])

  const [selected, setSelected] = useState<string[]>([])
  useEffect(() => {
    if (person.data) setSelected(person.data.groups)
  }, [person.data])

  const save = useMutation({
    mutationFn: (groups: string[]) =>
      api.updatePerson(uid, {
        first_name: person.data?.first_name,
        last_name: person.data?.last_name,
        external_ref: person.data?.external_ref,
        emails: person.data?.emails,
        phones: person.data?.phones,
        status: person.data?.status,
        groups,
        valid_from: person.data?.valid_from ?? null,
        valid_to: person.data?.valid_to ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [scope] })
      navigate(`/directory/${uid}`)
    },
  })

  if (person.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-56" />
      </div>
    )
  }
  if (person.isError || !person.data) {
    return (
      <EmptyState
        title="Person not found"
        hint={person.error ? errText(person.error) : uid}
        action={
          <Button variant="ghost" size="sm" onClick={() => navigate('/directory')}>
            ← All people
          </Button>
        }
      />
    )
  }

  const p = person.data
  const dirty =
    selected.length !== p.groups.length || selected.some((g) => !p.groups.includes(g))
  // A person bound to a dry-run system is read-only — membership saves too.
  const personReadOnly = readOnlyReason(p)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Directory" title={`Membership — ${p.display_name || 'Unnamed'}`}>
        <p className="tnum mt-1 text-xs text-ostrel-ink-muted">
          <Link to="/directory" className="hover:text-ostrel-gold">
            People
          </Link>
          {' / '}
          <Link to={`/directory/${p.unique_id}`} className="hover:text-ostrel-gold">
            {p.display_name || p.unique_id}
          </Link>
          {' / '}
          Membership
        </p>
      </PageHeader>

      <p className="text-[11px] text-ostrel-ink-muted">
        Membership changes take effect on access immediately.
      </p>

      {p.groups.length > 0 && (
        <Card title="Current memberships">
          <ul className="flex flex-col gap-1">
            {p.groups.map((g) => (
              <li key={g} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm text-ostrel-ink">
                  {groupPathMap.get(g) ?? g}
                </span>
                <Link
                  to={`/groups/${g}`}
                  className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
                >
                  View →
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Edit membership">
        <div className="flex flex-col gap-3">
          <GroupTreePicker selected={selected} onChange={setSelected} />
          {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/directory/${uid}`)}>
              Cancel
            </Button>
            <Button
              variant="navy"
              size="sm"
              disabled={save.isPending || !dirty || personReadOnly !== undefined}
              title={personReadOnly}
              onClick={() => save.mutate(selected)}
            >
              {save.isPending ? 'Saving…' : 'Save membership'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
